import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { MessageCircle, ChevronDown } from "lucide-react";
import { GYM, HERO_BG, waLink } from "@/lib/gymData";

const RIBBON = ["STRENGTH", "ENDURANCE", "DISCIPLINE", "POWER", "FOCUS", "GRIND"];

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="Athlete training" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-black/40" />
      </div>

      {/* Kinetic ribbon */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 select-none pointer-events-none">
        <Marquee autoFill speed={40}>
          {RIBBON.map((w, i) => (
            <span key={i} className="stroke-text font-display text-[14vw] leading-none mx-6">
              {w}
            </span>
          ))}
        </Marquee>
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 min-h-screen flex flex-col justify-center pt-20">
        <motion.img
          src={GYM.logoCircle}
          alt="Fitness Point Gym"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="h-24 sm:h-28 w-auto object-contain mb-6 drop-shadow-[0_8px_24px_rgba(0,0,0,0.6)]"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-blaze text-xs sm:text-sm uppercase tracking-[0.3em] mb-5"
        >
          Premium Fitness Center · {GYM.location}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-white leading-[0.85] tracking-tight text-6xl sm:text-8xl xl:text-[9rem] max-w-5xl"
        >
          TRANSFORM YOUR BODY,
          <br />
          <span className="text-blaze">TRANSFORM YOUR LIFE.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 max-w-xl text-neutral-300 text-sm sm:text-base leading-relaxed tracking-wide"
        >
          Where fitness meets dedication. Weight loss, muscle building, strength &
          cardio — with modern equipment and personalized guidance.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-join-button"
            className="inline-flex items-center gap-2 bg-blaze hover:bg-blaze-hover text-black uppercase tracking-[0.15em] font-semibold text-sm px-8 py-4 rounded-sm hover:shadow-[0_10px_30px_-8px_rgba(245,179,1,0.6)] transition-[background-color,box-shadow]"
          >
            <MessageCircle size={18} /> Join Now
          </a>
          <a
            href="#plans"
            data-testid="hero-plans-button"
            className="inline-flex items-center gap-2 border border-white/25 hover:border-blaze text-white uppercase tracking-[0.15em] font-medium text-sm px-8 py-4 rounded-sm hover:text-blaze transition-colors"
          >
            View Plans
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-neutral-400 animate-bounce">
        <ChevronDown size={26} />
      </div>
    </section>
  );
};
